import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * useDashboardPrefs — carga y persiste las preferencias del Dashboard por usuario.
 *
 * Estructura:
 *   prefs = { hidden: string[], order: string[], card_ids: string[] }
 *
 * Uso:
 *   const { prefs, isVisible, sortSection, save, reset } = useDashboardPrefs();
 */
export default function useDashboardPrefs() {
  const [prefs, setPrefs] = useState({
    hidden: [], order: [], card_ids: [],
    sidebar_collapsed: false, sidebar_auto_expand: false,
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings/dashboard-prefs")
      .then(({ data }) => { setPrefs(data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const isVisible = useCallback((id) => !prefs.hidden.includes(id), [prefs.hidden]);

  /**
   * Filtra y ordena una sección: recibe los IDs pertenecientes a la sección y
   * devuelve los visibles en el orden global del usuario.
   */
  const sortSection = useCallback((sectionIds) => {
    const inSection = new Set(sectionIds);
    const ordered = (prefs.order || []).filter((id) => inSection.has(id) && !prefs.hidden.includes(id));
    // Si algún id de la sección no está en `order` (por ejemplo un id nuevo), lo apendeamos
    for (const id of sectionIds) {
      if (!prefs.order?.includes(id) && !prefs.hidden.includes(id)) ordered.push(id);
    }
    return ordered;
  }, [prefs.order, prefs.hidden]);

  const save = useCallback(async (next) => {
    const { data } = await api.put("/settings/dashboard-prefs", {
      hidden: next.hidden ?? prefs.hidden ?? [],
      order: next.order ?? prefs.order ?? [],
      sidebar_collapsed: next.sidebar_collapsed ?? prefs.sidebar_collapsed ?? false,
      sidebar_auto_expand: next.sidebar_auto_expand ?? prefs.sidebar_auto_expand ?? false,
    });
    setPrefs(data);
    return data;
  }, [prefs]);

  /** Actualización parcial + persistencia. */
  const patch = useCallback(async (partial) => save({ ...prefs, ...partial }), [save, prefs]);

  const reset = useCallback(async () => {
    const { data } = await api.delete("/settings/dashboard-prefs");
    setPrefs(data);
    return data;
  }, []);

  return { prefs, loaded, isVisible, sortSection, save, patch, reset };
}
